import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { createTransfer, getScenes, getApiSettings } from '../services/api'
import { useAuth } from '@/contexts/AuthContext'
import Header from '@/components/Header'

export default function TransferCreate() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    recipient: '',
    amount: '',
    description: '',
    transferMode: 'REAL_TIME' as 'REAL_TIME' | 'USER_CONFIRM'
  })
  const [scenes, setScenes] = useState<Array<{ id: string | number; scene_id: string; name?: string; status?: string }>>([])
  const [sceneId, setSceneId] = useState<string>('1009')
  const [mchId, setMchId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const openid = params.get('openid') || ''
    const phone = params.get('phone') || ''
    if (openid) {
      setFormData((d) => ({ ...d, recipient: openid }))
    } else if (phone) {
      setFormData((d) => ({ ...d, recipient: phone }))
    }
    (async () => {
      try {
        const r = await getScenes()
        const list = (r.data?.scenes || []).filter((s: any) => s.status !== 'disabled')
        setScenes(list)
        const REAL_TIME_SCENES = ['1011','1002','1013','1004','1009','1010']
        const USER_CONFIRM_SCENES = ['1000','1005']
        const pickFirst = () => {
          const rt = list.find((s:any)=>REAL_TIME_SCENES.includes(String(s.scene_id)))
          if (rt) return rt.scene_id
          const uc = list.find((s:any)=>USER_CONFIRM_SCENES.includes(String(s.scene_id)))
          if (uc) return uc.scene_id
          return list[0]?.scene_id || '1009'
        }
        const preferred = pickFirst()
        setSceneId(preferred)
        setFormData(d => ({ ...d, transferMode: REAL_TIME_SCENES.includes(String(preferred)) ? 'REAL_TIME' : 'USER_CONFIRM' }))
      } catch {}
      try {
        const resp = await getApiSettings()
        const s = (resp.data && (resp.data.settings || resp.data)) || {}
        setMchId(String(s.wechatMchId || ''))
      } catch {}
    })()
  }, [location.search, user?.activeMerchantId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const response = await createTransfer({
        recipient: formData.recipient,
        amount: parseFloat(formData.amount),
        description: formData.description,
        transferMode: formData.transferMode,
        sceneId: sceneId as any,
        recipientName: (formData as any).recipientName
      })
      
      navigate(`/transfer/status/${response.data.transferId}`)
    } catch (error) {
      setMsg({ type: 'error', text: error instanceof Error ? error.message : '转账失败，请重试' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="发起转账" backTo="/dashboard" />

      {/* Main Content */}
      <main className="max-w-3xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg px-8 py-6">
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
            <div className="text-xs text-gray-500">当前商户</div>
            <div className="text-sm text-gray-900">微信商户号(MCHID): {mchId || '-'}</div>
          </div>
          {msg && (
            <div className={`mb-4 rounded border px-4 py-2 ${msg.type==='success' ? 'border-green-300 bg-green-50 text-green-700' : 'border-red-300 bg-red-50 text-red-700'}`}>{msg.text}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="recipient" className="block text-sm font-medium text-gray-700">
                收款人信息
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  id="recipient"
                  value={formData.recipient}
                  onChange={(e) => setFormData({ ...formData, recipient: e.target.value })}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-wechat-green focus:border-wechat-green sm:text-sm"
                  placeholder="请输入微信号或手机号"
                  required
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                可从绑定列表选择填充手机号；我们将在后端解析 openid
              </p>
              <div className="mt-2">
                <button type="button" onClick={()=>navigate('/bindings')} className="text-wechat-green hover:text-wechat-dark text-sm font-medium">
                  从绑定列表选择 →
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">选择转账场景</label>
              <div className="mt-1">
                <select
                  value={sceneId}
                  onChange={(e) => {
                    const v = e.target.value as any
                    setSceneId(v)
                    const REAL_TIME_SCENES = ['1011','1005','1009','1010','1002','1004']
                    setFormData(d => ({ ...d, transferMode: REAL_TIME_SCENES.includes(String(v)) ? 'REAL_TIME' : 'USER_CONFIRM' }))
                  }}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-wechat-green focus:border-wechat-green sm:text-sm"
                >
                  {scenes.length === 0 ? (
                    <option value="1009">1009（行政补贴）</option>
                  ) : (
                    scenes.map(s => (
                      <option key={String(s.id)} value={s.scene_id}>
                        {s.scene_id === '1000' ? '1000（现金营销）' :
                         s.scene_id === '1011' ? '1011（企业赔付）' :
                         s.scene_id === '1005' ? '1005（佣金报酬）' :
                         s.scene_id === '1009' ? '1009（采购货款）' :
                         s.scene_id === '1010' ? '1010（二手回收）' :
                         s.scene_id === '1013' ? '1013（公益补助）' :
                         s.scene_id === '1002' ? '1002（行政补贴）' :
                         s.scene_id === '1004' ? '1004（保险理赔）' : s.scene_id}{s.name ? ` - ${s.name}` : ''}
                     </option>
                    ))
                  )}
                </select>
              </div>
              <p className="mt-1 text-sm text-gray-500">仅可选择当前商户已获取的场景</p>
            </div>

            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                转账金额
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">¥</span>
                </div>
                {(() => { const minMap: Record<string, number> = { '1005': 1.00, '1004': 1.00 } ; const m = minMap[String(sceneId)] ?? 0.01 ; return (
                <input
                  type="number"
                  id="amount"
                  step="0.01"
                  min={m}
                  max="200000"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="appearance-none block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-wechat-green focus:border-wechat-green sm:text-sm"
                  placeholder="0.00"
                  required
                />) })()}
              </div>
              <p className="mt-1 text-sm text-gray-500">
                单笔最高20万元，最低{(['1005','1004'].includes(String(sceneId)) ? '1.00' : '0.01')}元（按场景）
              </p>
              {Number(formData.amount) > 2000 && (
                <div className="mt-3">
                  <label htmlFor="recipientName" className="block text-sm font-medium text-gray-700">收款人姓名（大于2000元必填）</label>
                  <input
                    type="text"
                    id="recipientName"
                    value={(formData as any).recipientName || ''}
                    onChange={(e) => setFormData({ ...formData, recipientName: e.target.value } as any)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-wechat-green focus:border-wechat-green sm:text-sm"
                    placeholder="请输入收款人姓名"
                  />
                </div>
              )}
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                转账备注
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-wechat-green focus:border-wechat-green sm:text-sm"
                  placeholder="请输入转账备注（用户可见）"
                  maxLength={32}
                  required
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                最多32个字符，用户收款时可见
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                转账方式
              </label>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    id="real-time"
                    name="transfer-mode"
                    type="radio"
                    value="REAL_TIME"
                    checked={formData.transferMode === 'REAL_TIME'}
                    onChange={(e) => setFormData({ ...formData, transferMode: e.target.value as any })}
                    className="focus:ring-wechat-green h-4 w-4 text-wechat-green border-gray-300"
                  />
                  <label htmlFor="real-time" className="ml-3 block text-sm font-medium text-gray-700">
                    实时到账
                  </label>
                </div>
                <div className="ml-7 text-sm text-gray-500">
                  资金立即转入用户账户，无需用户确认
                </div>

                <div className="flex items-center">
                  <input
                    id="user-confirm"
                    name="transfer-mode"
                    type="radio"
                    value="USER_CONFIRM"
                    checked={formData.transferMode === 'USER_CONFIRM'}
                    onChange={(e) => setFormData({ ...formData, transferMode: e.target.value as any })}
                    className="focus:ring-wechat-green h-4 w-4 text-wechat-green border-gray-300"
                  />
                  <label htmlFor="user-confirm" className="ml-3 block text-sm font-medium text-gray-700">
                    用户确认模式
                  </label>
                </div>
                <div className="ml-7 text-sm text-gray-500">
                  用户需要确认收款，更安全可靠
                </div>
              </div>
            </div>

            <div className="pt-5">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-wechat-green"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-wechat-green hover:bg-wechat-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-wechat-green disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? '处理中...' : '确认转账'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  )
}
